/**
 * PaymentTipSelector.jsx - Tip selection component
 */
import React, { useState, useCallback } from 'react';
import logger from './logger';

const TIP_PRESETS = [
  { percentage: 15, label: '15%' },
  { percentage: 20, label: '20%' },
  { percentage: 25, label: '25%' },
];

const formatCurrency = (amountCents, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amountCents / 100);
};

const PaymentTipSelector = ({
  subtotalCents,
  currentTipCents,
  currency = 'USD',
  onTipChange,
  isUpdating = false,
}) => {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);

  const presetAmounts = TIP_PRESETS.map((preset) => ({
    ...preset,
    amountCents: Math.round((subtotalCents * preset.percentage) / 100),
  }));

  const handlePresetClick = useCallback(
    (preset) => {
      if (isUpdating) return;

      setSelectedPreset(preset.percentage);
      setShowCustomInput(false);
      setCustomAmount('');

      const tipAmountCents = Math.round((subtotalCents * preset.percentage) / 100);
      logger.info('Tip preset selected:', { percentage: preset.percentage, tipAmountCents });
      onTipChange(tipAmountCents);
    },
    [subtotalCents, onTipChange, isUpdating]
  );

  const handleNoTip = useCallback(() => {
    if (isUpdating) return;

    setSelectedPreset(0);
    setShowCustomInput(false);
    setCustomAmount('');

    logger.info('No tip selected');
    onTipChange(0);
  }, [onTipChange, isUpdating]);

  const handleCustomClick = useCallback(() => {
    if (isUpdating) return;
    setSelectedPreset(null);
    setShowCustomInput(true);
  }, [isUpdating]);

  const handleCustomSubmit = useCallback(() => {
    if (isUpdating) return;

    const amountDollars = parseFloat(customAmount);
    if (!Number.isNaN(amountDollars) && amountDollars >= 0) {
      const tipAmountCents = Math.round(amountDollars * 100);
      logger.info('Custom tip submitted:', { amountDollars, tipAmountCents });
      onTipChange(tipAmountCents);
      setShowCustomInput(false);
    }
  }, [customAmount, onTipChange, isUpdating]);

  const handleCustomKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCustomSubmit();
      }
    },
    [handleCustomSubmit]
  );

  const isPresetSelected = (percentage) => {
    if (selectedPreset !== null) {
      return selectedPreset === percentage;
    }
    const presetAmount = Math.round((subtotalCents * percentage) / 100);
    return currentTipCents === presetAmount;
  };

  const isNoTipSelected = currentTipCents === 0 || selectedPreset === 0;

  // Check if current tip is a custom amount (not matching any preset)
  const isCustomTipApplied =
    currentTipCents > 0 &&
    !presetAmounts.some((preset) => preset.amountCents === currentTipCents) &&
    selectedPreset !== 0;

  return (
    <div className={`tip-selector ${isUpdating ? 'tip-selector--updating' : ''}`}>
      <div className="tip-selector-header">
        <span className="tip-selector-title">Add a tip</span>
      </div>

      <div className="tip-preset-grid">
        {presetAmounts.map((preset) => (
          <button
            key={preset.percentage}
            type="button"
            className={`tip-preset-btn ${isPresetSelected(preset.percentage) ? 'tip-preset-btn--selected' : ''}`}
            onClick={() => handlePresetClick(preset)}
            disabled={isUpdating}
          >
            <span className="tip-preset-percentage">{preset.label}</span>
            <span className="tip-preset-amount">
              {formatCurrency(preset.amountCents, currency)}
            </span>
          </button>
        ))}
      </div>

      {showCustomInput ? (
        <div className="tip-custom-input-wrapper">
          <span className="tip-custom-currency">$</span>
          <input
            type="number"
            className="tip-custom-input"
            placeholder="0.00"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            onKeyDown={handleCustomKeyDown}
            autoFocus
            min="0"
            step="0.01"
            disabled={isUpdating}
          />
          <button
            type="button"
            className="tip-custom-submit"
            onClick={handleCustomSubmit}
            disabled={isUpdating || !customAmount}
          >
            Apply
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={`tip-option-btn ${isCustomTipApplied ? 'tip-option-btn--selected' : ''}`}
          onClick={handleCustomClick}
          disabled={isUpdating}
        >
          {isCustomTipApplied
            ? `Tip: ${formatCurrency(currentTipCents, currency)}`
            : 'Custom Tip Amount'}
        </button>
      )}

      <button
        type="button"
        className={`tip-option-btn tip-no-tip-btn ${isNoTipSelected ? 'tip-option-btn--selected' : ''}`}
        onClick={handleNoTip}
        disabled={isUpdating}
      >
        No Tip
      </button>

      {isUpdating && (
        <div className="tip-updating-overlay">
          <div className="tip-updating-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default PaymentTipSelector;
